import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Brain,
  Sparkles,
  TrendingUp,
  Target,
  Users,
  Zap,
  BarChart3,
  RefreshCw,
  User,
  Building2,
  History,
  Lightbulb
} from "lucide-react";
import api from "../lib/api";

const AIPerformance = () => {
  const { currentBusiness } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [analysisType, setAnalysisType] = useState("comprehensive");
  const [loading, setLoading] = useState(false);
  const [teamLoading, setTeamLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [teamInsights, setTeamInsights] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (currentBusiness?.id) {
      fetchData();
    }
  }, [currentBusiness?.id]);

  const fetchData = async () => {
    try {
      const [empRes, deptRes, histRes] = await Promise.all([
        api.get(`/employees?business_id=${currentBusiness.id}`),
        api.get(`/departments?business_id=${currentBusiness.id}`),
        api.get(`/ai/analysis-history?business_id=${currentBusiness.id}`)
      ]);
      setEmployees(empRes.data);
      setDepartments(deptRes.data);
      setHistory(histRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  };

  const runAnalysis = async () => {
    if (!selectedEmployee) {
      toast.error("Please select an employee");
      return;
    }

    setLoading(true);
    setAnalysis(null);

    try {
      const response = await api.post(`/ai/performance-analysis?business_id=${currentBusiness.id}`, {
        employee_id: selectedEmployee,
        analysis_type: analysisType
      });
      setAnalysis(response.data);
      toast.success("Analysis complete!");
      fetchData(); // Refresh history
    } catch (error) {
      toast.error(error.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const runTeamInsights = async () => {
    setTeamLoading(true);
    setTeamInsights(null);

    try {
      const params = new URLSearchParams({ business_id: currentBusiness.id });
      if (selectedDepartment) {
        params.append('department_id', selectedDepartment);
      }
      
      const response = await api.post(`/ai/team-insights?${params.toString()}`);
      setTeamInsights(response.data);
      toast.success("Team insights generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate insights");
    } finally {
      setTeamLoading(false);
    }
  };

  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp ? `${emp.first_name} ${emp.last_name}` : "Unknown";
  };

  const formatAnalysis = (text) => {
    if (!text) return null;
    
    // Convert markdown-like formatting to HTML
    return text
      .split('\n')
      .map((line, i) => {
        // Headers
        if (line.startsWith('###')) {
          return <h4 key={i} className="font-semibold text-slate-800 mt-4 mb-2">{line.replace(/^###\s*/, '')}</h4>;
        }
        if (line.startsWith('##')) {
          return <h3 key={i} className="font-bold text-slate-800 text-lg mt-6 mb-3">{line.replace(/^##\s*/, '')}</h3>;
        }
        if (line.startsWith('#')) {
          return <h2 key={i} className="font-bold text-slate-800 text-xl mt-6 mb-3">{line.replace(/^#\s*/, '')}</h2>;
        }
        // Bullet points
        if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
          return <li key={i} className="ml-4 text-slate-600">{line.replace(/^[\s-•]+/, '')}</li>;
        }
        // Numbered lists
        if (/^\d+\./.test(line.trim())) {
          return <li key={i} className="ml-4 text-slate-600 list-decimal">{line.replace(/^\d+\.\s*/, '')}</li>;
        }
        // Bold text
        if (line.includes('**')) {
          const parts = line.split(/\*\*(.*?)\*\*/g);
          return (
            <p key={i} className="text-slate-600 my-1">
              {parts.map((part, j) => j % 2 === 1 ? <strong key={j}>{part}</strong> : part)}
            </p>
          );
        }
        // Empty lines
        if (!line.trim()) {
          return <br key={i} />;
        }
        // Regular text
        return <p key={i} className="text-slate-600 my-1">{line}</p>;
      });
  };

  if (!currentBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Building2 className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-2xl font-semibold text-slate-800 font-heading mb-2">No Business Selected</h2>
        <p className="text-slate-500">Select a business to use AI Performance Agent</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-performance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg shadow-purple-200">
            <Brain className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 font-heading flex items-center gap-2">
              AI Performance Agent
              <Sparkles className="h-5 w-5 text-amber-500" />
            </h1>
            <p className="text-slate-500">AI-powered employee performance insights</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="individual" className="space-y-6">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-full">
          <TabsTrigger value="individual" className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white" data-testid="individual-tab">
            <User className="h-4 w-4 mr-2" />
            Individual Analysis
          </TabsTrigger>
          <TabsTrigger value="team" className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white" data-testid="team-tab">
            <Users className="h-4 w-4 mr-2" />
            Team Insights
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white" data-testid="history-tab">
            <History className="h-4 w-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Individual Analysis Tab */}
        <TabsContent value="individual" className="space-y-6">
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Zap className="h-5 w-5 text-purple-500" />
                Run Performance Analysis
              </CardTitle>
              <CardDescription>
                Select an employee and analysis type to generate AI-powered insights
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Select Employee</label>
                  <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                    <SelectTrigger className="input-field" data-testid="ai-employee-select">
                      <SelectValue placeholder="Choose employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Analysis Type</label>
                  <Select value={analysisType} onValueChange={setAnalysisType}>
                    <SelectTrigger className="input-field" data-testid="ai-analysis-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="comprehensive">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="h-4 w-4" />
                          Comprehensive Analysis
                        </div>
                      </SelectItem>
                      <SelectItem value="trends">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4" />
                          Performance Trends
                        </div>
                      </SelectItem>
                      <SelectItem value="recommendations">
                        <div className="flex items-center gap-2">
                          <Lightbulb className="h-4 w-4" />
                          Recommendations
                        </div>
                      </SelectItem>
                      <SelectItem value="strengths">
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4" />
                          Strengths & Weaknesses
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button
                    onClick={runAnalysis}
                    disabled={loading || !selectedEmployee}
                    className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-full"
                    data-testid="run-analysis-btn"
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Brain className="h-4 w-4 mr-2" />
                        Run Analysis
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Analysis Results */}
          {analysis && (
            <Card className="card-soft border-purple-100" data-testid="analysis-results">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-purple-500" />
                      Analysis Results
                    </CardTitle>
                    <CardDescription>
                      {analysis.employee_name} • {analysis.analysis_type} analysis
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Badge className="bg-purple-100 text-purple-700">
                      Rating: {analysis.metrics?.avg_rating}/5
                    </Badge>
                    <Badge className="bg-green-100 text-green-700">
                      Attendance: {analysis.metrics?.attendance_rate}%
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="prose prose-slate max-w-none">
                  {formatAnalysis(analysis.analysis)}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Team Insights Tab */}
        <TabsContent value="team" className="space-y-6">
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-500" />
                Generate Team Insights
              </CardTitle>
              <CardDescription>
                Get AI-powered insights for your entire team or specific department
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Filter by Department (Optional)</label>
                  <Select value={selectedDepartment || "all"} onValueChange={(v) => setSelectedDepartment(v === "all" ? "" : v)}>
                    <SelectTrigger className="input-field" data-testid="ai-department-select">
                      <SelectValue placeholder="All departments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button
                    onClick={runTeamInsights}
                    disabled={teamLoading}
                    className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-full"
                    data-testid="run-team-insights-btn"
                  >
                    {teamLoading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generate Insights
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Team Insights Results */}
          {teamInsights && (
            <Card className="card-soft border-purple-100" data-testid="team-insights-results">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Users className="h-5 w-5 text-purple-500" />
                      Team Performance Insights
                    </CardTitle>
                    <CardDescription>
                      {teamInsights.team_size} employees analyzed
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Metrics Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 rounded-xl bg-purple-50">
                    <p className="text-sm text-slate-500">Avg Rating</p>
                    <p className="text-2xl font-bold text-purple-700">{teamInsights.metrics?.avg_rating}/5</p>
                  </div>
                  <div className="p-4 rounded-xl bg-green-50">
                    <p className="text-sm text-slate-500">Attendance</p>
                    <p className="text-2xl font-bold text-green-700">{teamInsights.metrics?.attendance_rate}%</p>
                  </div>
                  <div className="p-4 rounded-xl bg-blue-50">
                    <p className="text-sm text-slate-500">Team Size</p>
                    <p className="text-2xl font-bold text-blue-700">{teamInsights.team_size}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-amber-50">
                    <p className="text-sm text-slate-500">Pending Leaves</p>
                    <p className="text-2xl font-bold text-amber-700">{teamInsights.metrics?.pending_leaves}</p>
                  </div>
                </div>

                <div className="prose prose-slate max-w-none">
                  {formatAnalysis(teamInsights.insights)}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <History className="h-5 w-5 text-purple-500" />
                Analysis History
              </CardTitle>
              <CardDescription>
                Previous AI performance analyses
              </CardDescription>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Brain className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No analyses yet. Run your first analysis above!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {history.map((item) => (
                    <div
                      key={item.id}
                      className="p-4 rounded-xl border border-slate-100 hover:border-purple-200 hover:bg-purple-50/30 transition-colors cursor-pointer"
                      onClick={() => setAnalysis({
                        employee_id: item.employee_id,
                        employee_name: getEmployeeName(item.employee_id),
                        analysis_type: item.analysis_type,
                        analysis: item.analysis,
                        metrics: item.metrics
                      })}
                      data-testid={`history-item-${item.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                            <Brain className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">
                              {getEmployeeName(item.employee_id)}
                            </p>
                            <p className="text-sm text-slate-500 capitalize">
                              {item.analysis_type} Analysis
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">
                            {new Date(item.created_at).toLocaleDateString()}
                          </p>
                          <Badge variant="outline" className="mt-1">
                            Rating: {item.metrics?.avg_rating}/5
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIPerformance;
